import { Registry, type Identified } from './Registry';

/**
 * Yapı parçaları.
 * İki eksen var: KADEME (ahşap → güçlendirilmiş ahşap → taş → güçlendirilmiş taş)
 * ve VARYANT (dolu → pencere → kapı). Çekiç bu iki eksende dönüşüm yapar,
 * yani oyuncu yapıyı kırıp yeniden kurmak zorunda kalmaz.
 */

export type PieceShape = 'floor' | 'wall' | 'ceiling' | 'ramp' | 'stair' | 'fence' | 'roof' | 'station' | 'crop';
export type PieceVariant = 'solid' | 'window' | 'door';

export interface Cost {
  item: string;
  count: number;
}

export interface BuildPieceDef extends Identified {
  name: string;
  icon: string;
  shape: PieceShape;
  /** Kademe sırası (0 = ahşap). Yükseltme bu sırayı takip eder. */
  tier: number;
  tierId: string;
  variant: PieceVariant;
  cost: Cost[];
  durability: number;
  /** Üstünde yürünebilir mi? (zemin, tavan, rampa, merdiven) */
  walkable: boolean;
  /** Hareketi engeller mi? (kapı varyantı engellemez) */
  blocks: boolean;
  /** Bir sonraki kademedeki eşdeğer parça. */
  upgradeTo?: string;
  /** Aynı kademedeki diğer varyantlar (çekiç menüsü). */
  variantsOf?: string;
  desc?: string;
}

export const buildPieces = new Registry<BuildPieceDef>('buildPieces');

/** Izgara ölçüleri: tüm parçalar bu ölçülere birebir oturur. */
export const GRID = 4;
export const WALL_HEIGHT = 3;

interface TierSpec {
  id: string;
  name: string;
  order: number;
  durability: number;
  extra: Cost[];
}

const TIERS: TierSpec[] = [
  { id: 'wood', name: 'Ahşap', order: 0, durability: 250, extra: [{ item: 'wood', count: 4 }] },
  { id: 'wood_r', name: 'Güçlendirilmiş Ahşap', order: 1, durability: 550, extra: [{ item: 'wood', count: 6 }, { item: 'rope', count: 1 }] },
  { id: 'stone', name: 'Taş', order: 2, durability: 1100, extra: [{ item: 'stone', count: 6 }] },
  { id: 'stone_r', name: 'Güçlendirilmiş Taş', order: 3, durability: 2200, extra: [{ item: 'stone', count: 8 }, { item: 'iron', count: 2 }] },
];

interface ShapeSpec {
  shape: PieceShape;
  name: string;
  icon: string;
  walkable: boolean;
  blocks: boolean;
  /** Maliyet çarpanı. */
  mult: number;
  variants?: PieceVariant[];
}

const SHAPES: ShapeSpec[] = [
  { shape: 'floor', name: 'Zemin', icon: '🟫', walkable: true, blocks: false, mult: 1 },
  { shape: 'wall', name: 'Duvar', icon: '🧱', walkable: false, blocks: true, mult: 1, variants: ['solid', 'window', 'door'] },
  { shape: 'ceiling', name: 'Tavan', icon: '⬜', walkable: true, blocks: false, mult: 1 },
  { shape: 'ramp', name: 'Rampa', icon: '📐', walkable: true, blocks: false, mult: 1.25 },
  { shape: 'stair', name: 'Merdiven', icon: '🪜', walkable: true, blocks: false, mult: 1.25 },
  { shape: 'fence', name: 'Çit', icon: '🚧', walkable: false, blocks: true, mult: 0.5 },
  { shape: 'roof', name: 'Çatı', icon: '🏠', walkable: false, blocks: false, mult: 1.1 },
];

const VARIANT_LABEL: Record<PieceVariant, string> = { solid: '', window: 'Pencereli', door: 'Kapılı' };
const VARIANT_ICON: Record<PieceVariant, string> = { solid: '', window: '🪟', door: '🚪' };

function pieceId(tierId: string, shape: PieceShape, variant: PieceVariant): string {
  return variant === 'solid' ? `${tierId}_${shape}` : `${tierId}_${shape}_${variant}`;
}

for (const tier of TIERS) {
  for (const s of SHAPES) {
    const variants = s.variants ?? (['solid'] as PieceVariant[]);
    for (const variant of variants) {
      const id = pieceId(tier.id, s.shape, variant);
      const nextTier = TIERS.find((t) => t.order === tier.order + 1);
      const cost = tier.extra.map((c) => ({ item: c.item, count: Math.max(1, Math.round(c.count * s.mult)) }));
      // Pencere cam, kapı ek odun ister.
      if (variant === 'window') cost.push({ item: 'glass', count: 1 });
      if (variant === 'door') cost.push({ item: 'wood', count: 2 });
      buildPieces.register({
        id,
        name: `${tier.name} ${s.name}${variant === 'solid' ? '' : ' (' + VARIANT_LABEL[variant] + ')'}`,
        icon: VARIANT_ICON[variant] || s.icon,
        shape: s.shape,
        tier: tier.order,
        tierId: tier.id,
        variant,
        cost,
        durability: Math.round(tier.durability * s.mult),
        walkable: s.walkable,
        blocks: variant === 'door' ? false : s.blocks,
        upgradeTo: nextTier ? pieceId(nextTier.id, s.shape, variant) : undefined,
        variantsOf: s.variants ? `${tier.id}_${s.shape}` : undefined,
      });
    }
  }
}

// --- İstasyonlar ve tarım ---
buildPieces.register(
  {
    id: 'workbench',
    name: 'Üretim Masası',
    icon: '🛠️',
    shape: 'station',
    tier: 0,
    tierId: 'wood',
    variant: 'solid',
    cost: [{ item: 'wood', count: 20 }],
    durability: 400,
    walkable: false,
    blocks: true,
    desc: '8×8 üretim ızgarasını açar.',
  },
  {
    id: 'furnace',
    name: 'Fırın',
    icon: '🔥',
    shape: 'station',
    tier: 0,
    tierId: 'stone',
    variant: 'solid',
    cost: [{ item: 'stone', count: 16 }],
    durability: 600,
    walkable: false,
    blocks: true,
    desc: 'Eritme ve pişirme.',
  },
  {
    id: 'grinder',
    name: 'Kum Öğütücü',
    icon: '⚗️',
    shape: 'station',
    tier: 0,
    tierId: 'stone',
    variant: 'solid',
    cost: [{ item: 'stone', count: 12 }, { item: 'iron', count: 4 }],
    durability: 600,
    walkable: false,
    blocks: true,
    desc: 'Kumu ince kuma çevirir.',
  },
  {
    id: 'chest',
    name: 'Sandık',
    icon: '🧰',
    shape: 'station',
    tier: 0,
    tierId: 'wood',
    variant: 'solid',
    cost: [{ item: 'wood', count: 12 }],
    durability: 300,
    walkable: false,
    blocks: true,
    desc: 'Eşya saklar.',
  },
  {
    id: 'crop_wheat',
    name: 'Buğday Ekili Toprak',
    icon: '🌾',
    shape: 'crop',
    tier: 0,
    tierId: 'wood',
    variant: 'solid',
    cost: [{ item: 'wheat_seed', count: 1 }],
    durability: 20,
    walkable: false,
    blocks: false,
  },
  {
    id: 'crop_berry',
    name: 'Böğürtlen Ekili Toprak',
    icon: '🫐',
    shape: 'crop',
    tier: 0,
    tierId: 'wood',
    variant: 'solid',
    cost: [{ item: 'berry_seed', count: 1 }],
    durability: 20,
    walkable: false,
    blocks: false,
  },
);

/** Aynı kademedeki varyant listesi (çekiç menüsü için). */
export function variantsFor(pieceId: string): BuildPieceDef[] {
  const def = buildPieces.get(pieceId);
  if (!def || !def.variantsOf) return [];
  return buildPieces.filter((p) => p.variantsOf === def.variantsOf);
}

export function tierName(tierId: string): string {
  return TIERS.find((t) => t.id === tierId)?.name ?? tierId;
}

export const BUILD_TIERS = TIERS;
